import type { PlatformModuleId } from '@/types'

export type ModuleGroup = 'dashboards' | 'pre-closing' | 'post-closing' | 'tools'

export interface WorkflowModuleMeta {
  label: string
  short: string
  route: string
  stage: string
  description: string
  group: ModuleGroup
  icon: string
}

export const WORKFLOW_ORDER: PlatformModuleId[] = [
  // Dashboards (role-scoped executive boards)
  'dashboard-legal',
  'dashboard-underwriting',
  'dashboard-asset',
  'dashboard-ceo',
  // Pre-Closing (Pipeline)
  'command-center',
  'db-search',
  'pipeline',
  'intake',
  'credit-box',
  'golden-deal-reconciliation',
  'screening',
  'underwriting',
  'term-sheet',
  'committee',
  'closing',
  // Post-Closing (Asset Management)
  'asset-management',
  'surveillance',
  'reports',
  'portfolio-analytics',
  // Tools
  'valuation',
  'market-intelligence',
  'market-news',
  'crm',
  'loan-comparison',
  'hedge-desk',
  'document-vault',
  'investor-portal',
  'borrower-portal',
  'bi-dashboard',
  'comp-database',
  'workflow-automation',
]

export const WORKFLOW_META: Record<PlatformModuleId, WorkflowModuleMeta> = {
  // Named "Mgr" where they overlap an existing module (Underwriting = U/W,
  // Monitoring = MON) so the nav never carries two rows reading the same word.
  // Icons are unique across this map for the same reason: the collapsed 52px
  // rail shows the icon and nothing else, so a reused glyph is an unreadable
  // row. Scale/Calculator/Building2/Globe are already taken by committee,
  // underwriting, asset-management and market-intelligence.
  'dashboard-legal': {
    label: 'Risk Mgmt',
    short: 'RSK',
    route: '/dashboards/legal',
    stage: 'Risk Management',
    description: 'Matters, obligations, exceptions, audits, KYC and counsel spend in one briefing.',
    group: 'dashboards',
    icon: 'Gavel',
  },
  'dashboard-underwriting': {
    label: 'Underwriting Mgr',
    short: 'UWM',
    route: '/dashboards/underwriting',
    stage: 'Underwriting Desk',
    description: 'Desk queue, stage ageing, diligence completeness, and model readiness.',
    group: 'dashboards',
    icon: 'ClipboardCheck',
  },
  'dashboard-asset': {
    label: 'Asset Mgr',
    short: 'AMG',
    route: '/dashboards/asset',
    stage: 'Portfolio Oversight',
    description: 'Portfolio tiering, covenant test results, borrower requests, and maturities.',
    group: 'dashboards',
    icon: 'Warehouse',
  },
  'dashboard-ceo': {
    label: 'CEO',
    short: 'CEO',
    route: '/dashboards/ceo',
    stage: 'Executive Command',
    description: 'Production against plan, credit quality, capital deployment, and relationships.',
    group: 'dashboards',
    icon: 'Gauge',
  },
  'command-center': {
    label: 'Command Center',
    short: 'CMD',
    route: '/command-center',
    stage: 'Portfolio Command',
    description: 'Cross-portfolio control tower — pipeline pressure, KPIs, triggers, and funding readiness.',
    group: 'pre-closing',
    icon: 'LayoutDashboard',
  },
  'db-search': {
    label: 'DB Search',
    short: 'DBS',
    route: '/db-search',
    stage: 'Deal Locator',
    description: 'Every deal since inception in one query — filter, aggregate, compare periods, export to Excel.',
    group: 'pre-closing',
    icon: 'DatabaseZap',
  },
  pipeline: {
    label: 'Pipeline',
    short: 'PIP',
    route: '/pipeline',
    stage: 'Deal Book',
    description: 'Full deal pipeline with filtering, sorting, and deal-level detail panels.',
    group: 'pre-closing',
    icon: 'GitBranch',
  },
  intake: {
    label: 'Intake',
    short: 'INT',
    route: '/intake',
    stage: 'Borrower Intake',
    description: 'Broker/borrower request ingestion, triage, and initial deal creation.',
    group: 'pre-closing',
    icon: 'Inbox',
  },
  screening: {
    label: 'Screening',
    short: 'SCR',
    route: '/screening',
    stage: 'Credit Screening',
    description: 'Deal sizing, mandate fit analysis, and kill-or-advance decisions.',
    group: 'pre-closing',
    icon: 'ScanSearch',
  },
  underwriting: {
    label: 'Underwriting',
    short: 'U/W',
    route: '/underwriting',
    stage: 'Underwriting',
    description: 'Asset-class-specific cash flow analysis, stress testing, structure, and covenant shaping.',
    group: 'pre-closing',
    icon: 'Calculator',
  },
  'term-sheet': {
    label: 'Term Sheets',
    short: 'T/S',
    route: '/term-sheet',
    stage: 'Term Sheet Generation',
    description: 'Auto-generate professional term sheets from deal data with live preview.',
    group: 'pre-closing',
    icon: 'FileSignature',
  },
  committee: {
    label: 'Committee',
    short: 'COM',
    route: '/committee',
    stage: 'Committee Review',
    description: 'Credit memo production, approver routing, and conditions precedent.',
    group: 'pre-closing',
    icon: 'Scale',
  },
  closing: {
    label: 'Funding',
    short: 'FND',
    route: '/closing',
    stage: 'Closing & Funding',
    description: 'Legal, rate lock, checklist tracking, fee calculation, and wire release.',
    group: 'pre-closing',
    icon: 'Lock',
  },
  'asset-management': {
    label: 'Monitoring',
    short: 'MON',
    route: '/asset-management',
    stage: 'Asset Monitoring',
    description: 'Ongoing asset performance tracking, DCF analysis, budget monitoring, and portfolio consolidation.',
    group: 'post-closing',
    icon: 'Building2',
  },
  surveillance: {
    label: 'Surveillance',
    short: 'SRV',
    route: '/surveillance',
    stage: 'Post-Close Surveillance',
    description: 'Covenant compliance, watchlists, trigger management, and borrower reporting.',
    group: 'post-closing',
    icon: 'Eye',
  },
  reports: {
    label: 'Reporting',
    short: 'RPT',
    route: '/reports',
    stage: 'Reporting',
    description: 'ASR generation, scheduled reports, portfolio summaries, and investor packages.',
    group: 'post-closing',
    icon: 'FileText',
  },
  'portfolio-analytics': {
    label: 'Portfolio Analytics',
    short: 'ANL',
    route: '/portfolio-analytics',
    stage: 'Portfolio Analytics',
    description: 'Risk heatmaps, concentration analysis, stress testing, LTV analysis, and market exposure.',
    group: 'post-closing',
    icon: 'PieChart',
  },
  valuation: {
    label: 'Valuation',
    short: 'VAL',
    route: '/valuation',
    stage: 'Asset Valuation',
    description: 'Property valuation management, benchmarking, and performance attribution.',
    group: 'tools',
    icon: 'TrendingUp',
  },
  'market-intelligence': {
    label: 'Market Intel',
    short: 'MKT',
    route: '/market-intel',
    stage: 'Market & Models',
    description: 'External model comparison, market data feeds, and risk overlays.',
    group: 'tools',
    icon: 'Globe',
  },
  'market-news': {
    label: 'News & Intel',
    short: 'NEWS',
    route: '/market-news',
    stage: 'Market News',
    description: 'Real-time CRE market news, sector intelligence, and entity tracking alerts.',
    group: 'tools',
    icon: 'Newspaper',
  },
  crm: {
    label: 'CRM',
    short: 'CRM',
    route: '/crm',
    stage: 'Relationships',
    description: 'AI-powered contact and relationship management with auto-enrichment.',
    group: 'tools',
    icon: 'Users',
  },
  'loan-comparison': {
    label: 'Loan Compare',
    short: 'CMP',
    route: '/loan-comparison',
    stage: 'Deal Structuring',
    description: 'Side-by-side comparison of deal structures with stress testing and guideline checks.',
    group: 'tools',
    icon: 'GitCompare',
  },
  'hedge-desk': {
    label: 'Hedge Desk',
    short: 'HDG',
    route: '/hedge-desk',
    stage: 'Rate Management',
    description: 'Floating rate exposure tracking, interest rate cap modeling, and hedge analytics.',
    group: 'tools',
    icon: 'Shield',
  },
  'document-vault': {
    label: 'Documents',
    short: 'DOC',
    route: '/documents',
    stage: 'Document Management',
    description: 'Centralized document vault with version control, ingestion, and data extraction.',
    group: 'tools',
    icon: 'FolderOpen',
  },
  'investor-portal': {
    label: 'Investors',
    short: 'INV',
    route: '/investor-portal',
    stage: 'Investor Relations',
    description: 'Read-only LP portal showing portfolio NAV, distributions, investment performance, and quarterly reports.',
    group: 'tools',
    icon: 'Landmark',
  },
  'borrower-portal': {
    label: 'Borrowers',
    short: 'BRW',
    route: '/borrower-portal',
    stage: 'Borrower Workspaces',
    description: "Staff view of each borrower's application workspace: outstanding documents, uploads and loan tracking. Staff sign-in required; borrowers cannot sign in yet.",
    group: 'tools',
    icon: 'UserCheck',
  },
  'bi-dashboard': {
    label: 'BI Dashboard',
    short: 'BI',
    route: '/bi-dashboard',
    stage: 'Business Intelligence',
    description: 'Deep analytics with origination volume, conversion funnels, originator scorecards, and revenue attribution.',
    group: 'tools',
    icon: 'BarChart3',
  },
  'comp-database': {
    label: 'Comp DB',
    short: 'CDB',
    route: '/comp-database',
    stage: 'Comparable Transactions',
    description: 'Internal comparable transaction database that grows with every deal.',
    group: 'tools',
    icon: 'Database',
  },
  'golden-deal-reconciliation': {
    label: 'Golden-Deal Recon',
    short: 'GDR',
    route: '/golden-deal-reconciliation',
    stage: 'Calibration',
    description: 'Your golden deals run through the platform against your own credit decisions.',
    group: 'pre-closing',
    icon: 'Scale',
  },
  'credit-box': {
    label: 'Credit Box',
    short: 'CBX',
    route: '/credit-box',
    stage: 'Calibration',
    description: 'The versioned ruleset every deal is screened against — programs, thresholds, judgment factors.',
    group: 'pre-closing',
    icon: 'ShieldCheck',
  },
  'workflow-automation': {
    label: 'Automation',
    short: 'AUT',
    route: '/automation',
    stage: 'Workflow Automation',
    description: 'Configurable automation triggers with no-code rule builder.',
    group: 'tools',
    icon: 'Workflow',
  },
}

/**
 * Post-login module per role.
 *
 * A module id rather than a route so RoleLanding can run it through the same
 * canView check the nav uses — a role whose landing module is not in the
 * license must fall through to the ordered fallback, not land on the
 * not-licensed screen with no way forward.
 *
 * Only the roles introduced with the dashboards appear here. Every role that
 * existed before — admin, analyst, viewer, model_editor, model_admin — is
 * absent on purpose, so RoleLanding falls through to /command-center and they
 * land exactly where they landed when the index route was a hardcoded
 * Navigate.
 */
export const ROLE_LANDING: Record<string, PlatformModuleId> = {
  general_counsel: 'dashboard-legal',
  chief_executive: 'dashboard-ceo',
  underwriting_manager: 'dashboard-underwriting',
  asset_manager: 'dashboard-asset',
}

export function getWorkflowMeta(moduleId: PlatformModuleId) {
  return WORKFLOW_META[moduleId]
}

/**
 * What the Layout header names the current page.
 *
 * The header used getWorkflowModuleByPath, whose 'command-center' fallback is
 * right for nothing but nav highlighting: /settings was headed "Command Center ·
 * CMD · Portfolio Command" above a body reading "SETTINGS · Administration",
 * and an unknown URL claimed to be the Command Center too. Pages that are not
 * workflow modules name themselves here instead.
 */
export function headerMetaForPath(
  pathname: string,
): Pick<WorkflowModuleMeta, 'label' | 'short' | 'stage'> {
  const moduleId = findWorkflowModuleByPath(pathname)
  if (moduleId) return WORKFLOW_META[moduleId]
  const path = pathname.replace(/\/+$/, '') || '/'
  if (path === '/settings' || path.startsWith('/settings/')) {
    return { label: 'Settings', short: 'SET', stage: 'Administration' }
  }
  // The People & Team page is reached from the CEO and Underwriting Mgr boards
  // rather than its own nav row; its API admits either board's permission.
  if (path === '/dashboards/people') {
    return { label: 'People & Team', short: 'PPL', stage: 'Desk Analytics' }
  }
  // The model-template governance page (UI-04) is reached from the Intake adopt
  // chain and the Settings profile, not from its own nav row.
  if (path === '/model-templates') {
    return { label: 'Model Templates', short: 'TPL', stage: 'Borrower Intake' }
  }
  if (path === '/') return { label: '', short: '', stage: '' }
  return { label: 'Page not found', short: '404', stage: '' }
}

export function getWorkflowModuleByPath(pathname: string): PlatformModuleId {
  const match = WORKFLOW_ORDER.find((moduleId) => pathname.startsWith(WORKFLOW_META[moduleId].route))
  return match ?? 'command-center'
}

/**
 * The module this path belongs to, or null when it belongs to none.
 *
 * getWorkflowModuleByPath answers 'command-center' for anything unrecognised,
 * which is right for highlighting the active nav item and wrong for deciding
 * whether to let the page render: /settings is not the Command Center, and a
 * permission check built on that fallback would lock a license out of its own
 * profile page the moment Command Center was de-permissioned.
 *
 * Matches on a segment boundary so a future /pipeline-archive route cannot
 * inherit /pipeline's answer.
 */
export function findWorkflowModuleByPath(pathname: string): PlatformModuleId | null {
  const path = pathname.replace(/\/+$/, '') || '/'
  return (
    WORKFLOW_ORDER.find((moduleId) => {
      const route = WORKFLOW_META[moduleId].route
      return path === route || path.startsWith(`${route}/`)
    }) ?? null
  )
}

/**
 * The dossier header's back link, named for the screen the dossier closes
 * back to. The dossier is an overlay on whatever screen opened it, and the
 * link read "Back to Pipeline" over the Deal Locator and Intake as well
 * (nd-033). A path that names no screen gets a plain "Back".
 */
export function dossierBackLabel(pathname: string): string {
  const { label } = headerMetaForPath(pathname)
  return label && label !== 'Page not found' ? `Back to ${label}` : 'Back'
}
